import {
    FunctionComponent,
    PropsWithChildren,
    useMemo,
    ComponentProps,
    lazy,
} from "react";

import { AuthorizationService } from "@/services/authorization-service";
import { useUser } from "@/contexts/user";
import useHasFeatureFlags from "@/hooks/useHasFeatureFlags";
import { canAccessAllOrgsAndDevices } from "@/utils/user-permission";
import { Spinner } from "../ui/spinner";
import { SearchCheck } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import ErrorUI from "../ui/error-ui";
import { getPermissionInspectorEnabled } from "@/utils/utils";
import UnauthorisedPage from "@/pages/unauthorised-page";
import { IlluminateUserPermissionsEnum } from "@/api/openapi";
import { FeatureFlag } from "@/constants";
import { Button } from "../ui/button";
const TermsAndConditionsPage = lazy(
    () => import("@/pages/terms-and-conditions-page/terms-and-conditions-page"),
);

/**
 * Access guard component properties.
 */
export interface AccessGuardProps {
    /**
     * Optional Illuminate permissions required for the route/nav item
     */
    permissions?: IlluminateUserPermissionsEnum[];
    /**
     * Selection mode for permissions, default is all.
     * all: user must have all tags in the list.
     * any: user must have at least one of the tags in the list
     */
    permissionsSelector?: "all" | "any";
    /**
     * Optional Illuminate access tags (OrganisationId and OrganisationGroupIds) required for the item
     */
    accessTags?: string[];
    /**
     * Selection mode for accessTags, default is all.
     * all: user must have all tags in the list.
     * any: user must have at least one of the tags in the list
     */
    accessTagsSelector?: "all" | "any";
    /**
     * Optional LaunchDarkly feature flags required for the item.
     * Required for most new features without explicit permission, until the feature is stable.
     */
    featureFlags?: FeatureFlag[];
    /**
     * Selection mode for featureFlags, default is all.
     * Use "none" to pass when the flag is off.
     */
    featureFlagsSelector?: "all" | "any" | "none";
    /**
     * When an element is unauthorized, should the user be redirected
     * to the unauthorized page, or should the element be hidden
     */
    unauthorizedBehaviour: "unauthorizedPage" | "hide";
    /**
     * When user is authorised, but the feature flag is off, user is treated as unauthorised.
     * Use this prop to show an alternate UI instead.
     */
    flagOffElement?: React.ReactNode;
    /**
     * Restrict the UI based on a condition.
     * If true, user is treated as unauthorised.
     */
    overrideAndRestrict?: boolean;
    showFullScreenLoadSpinner?: boolean;
    skipTermsAndConditionsCheck?: boolean;
}

const useAccessGuard = (
    props: Omit<ComponentProps<typeof AccessGuard>, "unauthorizedBehaviour">,
) => {
    const { user, isLoading: isUserLoading } = useUser();

    const hasFeatureFlags = useHasFeatureFlags();

    const isCheckingUserAccess =
        !!props.accessTags?.length || !!props.permissions?.length;

    const featureFlagsEnabled = hasFeatureFlags(
        props.featureFlags ?? [],
        props.featureFlagsSelector ?? "all",
    );

    const hasAccessTags = AuthorizationService.hasAccessTags(
        props.accessTags ?? [],
        user?.accessTags ?? [],
        props.accessTagsSelector ?? "all",
    );

    const hasPermissions = AuthorizationService.hasPermissions(
        props.permissions ?? [],
        user?.permissions ?? [],
        props.permissionsSelector ?? "all",
    );

    if (isCheckingUserAccess && isUserLoading) {
        return { isAuthorized: false, isLoading: true };
    }

    if (!isCheckingUserAccess) {
        return {
            isAuthorized: featureFlagsEnabled || !!props.flagOffElement,
            isLoading: false,
        };
    }

    if (
        user &&
        hasAccessTags &&
        hasPermissions &&
        (featureFlagsEnabled || props.flagOffElement)
    ) {
        return { isAuthorized: true, isLoading: false };
    }

    if (user) {
        return { isAuthorized: false, isLoading: false };
    }

    return { isAuthorized: false, isLoading: isUserLoading };
};

/**
 * Secures UI based on a user's authorisation.
 * Renders children if the user has the required permissions/flags,
 * otherwise renders unauthorisedElement, or redirects to /unauthorised.
 *
 * If user is authorised, but has not accepted terms and conditions,
 * shows the accept t&c page
 * @param props The access guard properties.
 * @returns A react fragment.
 */
const AccessGuard: FunctionComponent<PropsWithChildren<AccessGuardProps>> = (
    props,
) => {
    const {
        user,
        error: userError,
        refetch: refetchUser,
        isFetching: isFetchingUser,
    } = useUser();

    const hasFeatureFlags = useHasFeatureFlags();
    const featureFlagsEnabled = hasFeatureFlags(
        props.featureFlags ?? [],
        props.featureFlagsSelector ?? "all",
    );

    const { isAuthorized, isLoading } = useAccessGuard(props);

    const termsAndConditionsAccepted = useMemo(
        () => canAccessAllOrgsAndDevices(user) || !!user?.termsAcceptedAt,
        [user],
    );

    const showPermissionsInspector = useMemo(
        () =>
            getPermissionInspectorEnabled() &&
            (user?.roles.includes("system_administrator") ||
                user?.roles.includes("role_preview")),
        [user?.roles],
    );

    const formatSelectors = (
        selector: string | undefined,
        items: Array<string>,
    ) => {
        const s = selector ?? "all";
        if (items.length == 1 && (s === "any" || s === "all")) return items[0];
        return `${s}: [${items.join(", ")}]`;
    };
    const onInspect = () => {
        const msgs: Array<string> = [];
        if (props.permissions)
            msgs.push(
                `Permissions: ${formatSelectors(props.permissionsSelector, props.permissions)}`,
            );
        if (props.featureFlags)
            msgs.push(
                `Flags: ${formatSelectors(props.featureFlagsSelector, props.featureFlags)}`,
            );
        if (props.accessTags)
            msgs.push(
                `Tags: ${formatSelectors(props.accessTagsSelector, props.accessTags)}`,
            );
        toast({
            title: `Permission Inspector`,
            description: msgs.map((m) => (
                <>
                    {m}
                    <br />
                </>
            )),
            appearance: "information",
        });
    };

    if (isLoading) {
        return (
            <Spinner
                cover={!!props.showFullScreenLoadSpinner}
                sizing={props.showFullScreenLoadSpinner ? "largest" : "default"}
            />
        );
    } else if (isAuthorized && !props.overrideAndRestrict) {
        const element = featureFlagsEnabled
            ? props.children
            : (props.flagOffElement ?? null);
        if (termsAndConditionsAccepted || props.skipTermsAndConditionsCheck) {
            return showPermissionsInspector ? (
                //div with button on top of element:
                <div className="relative flex">
                    {element}

                    <SearchCheck
                        className="bg-danger-press absolute top-0 right-3 -mr-3 flex h-3 w-3 cursor-pointer p-0.5"
                        spacing="compact"
                        onClick={onInspect}
                    />
                </div>
            ) : (
                element
            );
        } else {
            // Terms and conditions not accepted
            return (
                <div className="w-full">
                    <TermsAndConditionsPage />
                </div>
            );
        }
    } else if (props.unauthorizedBehaviour === "hide") {
        return null; // Show nothing
    } else if (userError) {
        return (
            <ErrorUI
                type="cover"
                title="Unable to fetch user"
                description={
                    userError?.message ||
                    "We ran into an issue checking your permissions."
                }
                actionButtons={[
                    <Button
                        key={"refetch"}
                        appearance="brand"
                        onClick={refetchUser}
                        disabled={isFetchingUser}
                    >
                        Retry
                    </Button>,
                ]}
            />
        );
    } else {
        return <UnauthorisedPage />;
    }
};

export default AccessGuard;
